import { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import whiteLogo from '@assets/fiber_methods_logo_1759154176778.png';
import blackLogo from '@assets/fiber_methods_logo_black_1759157141807.png';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    console.log('Mobile menu toggled');
  };

  const handleNavClick = (section) => {
    console.log(`Navigating to ${section}`);
    setIsMenuOpen(false);
    
    // Scroll to the appropriate section
    if (section === 'services') {
      const servicesElement = document.getElementById('services');
      if (servicesElement) {
        servicesElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (section === 'contact') {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (section === 'about') {
      const aboutElement = document.getElementById('about');
      if (aboutElement) {
        aboutElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (section === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    // Check for dark mode
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };

    // Preload alternate logo to prevent flicker
    const img = new Image();
    img.src = blackLogo;
    const img2 = new Image();
    img2.src = whiteLogo;

    window.addEventListener('scroll', handleScroll);
    checkDarkMode();
    
    // Listen for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 border-b z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-slate-900/60' 
        : 'bg-slate-900 dark:bg-slate-900'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => handleNavClick('home')}
              className="flex items-center"
              aria-label="Fiber Methods Home"
              data-testid="logo-home-button"
            >
              <img 
                src={isScrolled && !isDarkMode ? blackLogo : whiteLogo}
                alt="Fiber Methods" 
                className="h-10 w-auto transition-opacity duration-300"
                width="108"
                height="40"
                decoding="async"
                fetchPriority={isScrolled && !isDarkMode ? undefined : 'high'}
                data-testid="logo-fiber-methods"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => handleNavClick('home')}
              className={`transition-colors ${isScrolled ? 'text-foreground hover:text-primary' : 'text-white hover:text-white/80'}`}
              data-testid="nav-home"
            >
              Home
            </button>
            <button 
              onClick={() => handleNavClick('services')}
              className={`transition-colors ${isScrolled ? 'text-foreground hover:text-primary' : 'text-white hover:text-white/80'}`}
              data-testid="nav-services"
            >
              Services
            </button>
            <button 
              onClick={() => handleNavClick('about')}
              className={`transition-colors ${isScrolled ? 'text-foreground hover:text-primary' : 'text-white hover:text-white/80'}`}
              data-testid="nav-about"
            >
              About
            </button>
            <button 
              onClick={() => handleNavClick('contact')}
              className={`transition-colors ${isScrolled ? 'text-foreground hover:text-primary' : 'text-white hover:text-white/80'}`}
              data-testid="nav-contact"
            >
              Contact
            </button>
          </nav>

          {/* Contact Info - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className={`flex items-center space-x-2 ${isScrolled ? 'text-foreground' : 'text-white'}`}>
              <Phone className="h-4 w-4" />
              <a href="tel:+17042755737" className="text-sm font-medium hover:text-primary transition-colors" data-testid="phone-link-desktop">(704) 275-5737</a>
            </div>
            <div className={`flex items-center space-x-2 ${isScrolled ? 'text-foreground' : 'text-white'}`}>
              <Mail className="h-4 w-4" />
              <a href="mailto:info@fiberm.com" className="text-sm font-medium hover:text-primary transition-colors" data-testid="email-link-desktop">info@fiberm.com</a>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={toggleMenu}
            className={`md:hidden p-2 rounded-md transition-colors ${isScrolled ? 'text-foreground hover:bg-accent' : 'text-white hover:bg-white/10'}`}
            data-testid="button-mobile-menu"
          >
            <span className="sr-only">Open main menu</span>
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleNavClick('home')}
                className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-primary hover:bg-accent rounded-md"
                data-testid="mobile-nav-home"
              >
                Home
              </button>
              <button
                onClick={() => handleNavClick('services')}
                className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-primary hover:bg-accent rounded-md"
                data-testid="mobile-nav-services"
              >
                Services
              </button>
              <button
                onClick={() => handleNavClick('about')}
                className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-primary hover:bg-accent rounded-md"
                data-testid="mobile-nav-about"
              >
                About
              </button>
              <button
                onClick={() => handleNavClick('contact')}
                className="block w-full text-left px-3 py-2 text-base font-medium text-foreground hover:text-primary hover:bg-accent rounded-md"
                data-testid="mobile-nav-contact"
              >
                Contact
              </button>
              
              {/* Mobile Contact Info */}
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <a href="tel:+17042755737" className="hover:text-primary transition-colors" data-testid="phone-link-mobile">(704) 275-5737</a>
                </div>
                <div className="flex items-center space-x-2 px-3 py-2 text-sm text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:info@fiberm.com" className="hover:text-primary transition-colors" data-testid="email-link-mobile">info@fiberm.com</a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}